/**
 * Aperçu d'une journée de suivi, ouvert depuis un calendrier.
 *
 * Partagé entre le calendrier de la page Cycle et celui des humeurs (page Mental), qui
 * répondaient à la même question de deux façons différentes : le premier n'apercevait
 * que le cycle — une journée portant du mental ou du sport, mais aucun relevé de cycle,
 * affichait « Aucune donnée enregistrée » suivie du seul bouton de saisie — et le second
 * n'était pas cliquable du tout.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { buildDetailRows } from '../constants/dayDetails';
import { formatDateLabel } from '../utils/formDate';
import './DayDetailModal.css';

const SECTIONS = [
  { id: 'cycle', title: 'Cycle', form: '/formulaire/cycle' },
  { id: 'mental', title: 'Mental', form: '/formulaire/mental' },
  { id: 'sport', title: 'Sport', form: '/formulaire/sport' },
];

const DayDetailModal = ({
  date,
  entries = {},
  phaseLabel = null,
  phaseColor = null,
  isFuture = false,
  onClose,
}) => {
  const navigate = useNavigate();
  if (!date) return null;

  const sections = SECTIONS.map((section) => {
    const entry = entries[section.id];
    return {
      ...section,
      entry,
      isRest: Boolean(entry?.is_rest_day),
      rows: entry && !entry.is_rest_day ? buildDetailRows(section.id, entry) : [],
    };
  });

  const hasAnything = sections.some((s) => s.rows.length > 0 || s.isRest);

  return (
    <div className="day-modal__overlay" onClick={onClose}>
      <div
        className="day-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Journée du ${formatDateLabel(date)}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="day-modal__header">
          <span className="day-modal__date">{formatDateLabel(date)}</span>
          <button className="day-modal__close" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        {phaseLabel && (
          <span
            className="day-modal__phase"
            style={phaseColor ? { background: `${phaseColor}22`, color: phaseColor } : undefined}
          >
            {phaseLabel}
          </span>
        )}

        {hasAnything ? (
          <div className="day-modal__sections">
            {sections.map((section) => {
              if (!section.rows.length && !section.isRest) return null;
              return (
                <div key={section.id} className="day-modal__section">
                  <span className="day-modal__section-title">{section.title}</span>
                  {section.isRest ? (
                    <p className="day-modal__rest">Jour de repos</p>
                  ) : (
                    section.rows.map(([label, value]) => (
                      <div key={label} className="day-modal__row">
                        <span>{label}</span>
                        <span>{value}</span>
                      </div>
                    ))
                  )}
                  {section.entry?.notes && (
                    <p className="day-modal__note">{section.entry.notes}</p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="day-modal__empty">Aucune donnée enregistrée</p>
        )}

        {/* Un jour à venir ne se remplit pas : les formulaires refusent une date future. */}
        {!isFuture && (
          <div className="day-modal__actions">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                className="day-modal__btn"
                onClick={() => {
                  onClose();
                  navigate(`${section.form}?date=${date}`);
                }}
              >
                {entries[section.id] ? `Modifier ${section.title.toLowerCase()}` : `Ajouter ${section.title.toLowerCase()}`}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DayDetailModal;
